import { ReactNode } from 'react'

/**
 * Renderizador de Markdown mínimo y sin dependencias para documentos propios
 * (legales y procedimientos de mantenimiento). Soporta: encabezados, blockquote,
 * reglas, listas (con viñetas y numeradas, con sub-viñetas), tablas GFM, bloques
 * de código ``` y, en línea, **negrita**, `código` y [enlaces](url).
 */

function renderInline(text: string, keyPrefix: string): ReactNode[] {
    const nodes: ReactNode[] = []
    // Patrón combinado: **bold**, `código` o [texto](url)
    const regex = /\*\*(.+?)\*\*|`([^`]+)`|\[(.+?)\]\((.+?)\)/g
    let lastIndex = 0
    let match: RegExpExecArray | null
    let i = 0
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index))
        if (match[1] !== undefined) {
            nodes.push(<strong key={`${keyPrefix}-b${i}`}>{match[1]}</strong>)
        } else if (match[2] !== undefined) {
            nodes.push(
                <code key={`${keyPrefix}-c${i}`} className="rounded bg-gray-800 px-1.5 py-0.5 font-mono text-[0.85em] text-gray-100">
                    {match[2]}
                </code>,
            )
        } else {
            nodes.push(
                <a key={`${keyPrefix}-a${i}`} href={match[4]} className="text-indigo-400 underline">
                    {match[3]}
                </a>,
            )
        }
        lastIndex = regex.lastIndex
        i++
    }
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
    return nodes
}

function splitRow(line: string): string[] {
    return line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
}

export default function Markdown({ content }: { content: string }) {
    const lines = content.split('\n')
    const blocks: ReactNode[] = []
    let i = 0
    let key = 0

    while (i < lines.length) {
        const line = lines[i]

        // Bloque de código ``` (se muestra tal cual, sin formato en línea)
        if (line.trim().startsWith('```')) {
            const code: string[] = []
            i++
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                code.push(lines[i])
                i++
            }
            i++ // cierre ```
            blocks.push(
                <pre key={key++} className="my-4 overflow-x-auto rounded-lg border border-gray-700 bg-gray-950 p-4 text-xs leading-relaxed">
                    <code className="font-mono text-gray-100">{code.join('\n')}</code>
                </pre>,
            )
            continue
        }

        // Listas numeradas (con sub-viñetas indentadas "   - ...")
        if (/^\d+\.\s+/.test(line)) {
            const items: { text: string; sub: string[] }[] = []
            while (i < lines.length && (/^\d+\.\s+/.test(lines[i]) || /^\s{2,}-\s+/.test(lines[i]))) {
                if (/^\d+\.\s+/.test(lines[i])) {
                    items.push({ text: lines[i].replace(/^\d+\.\s+/, ''), sub: [] })
                } else if (items.length > 0) {
                    items[items.length - 1].sub.push(lines[i].replace(/^\s+-\s+/, ''))
                }
                i++
            }
            blocks.push(
                <ol key={key++} className="list-decimal pl-6 my-3 space-y-1">
                    {items.map((it, ii) => (
                        <li key={ii}>
                            {renderInline(it.text, `ol${key}-${ii}`)}
                            {it.sub.length > 0 && (
                                <ul className="list-disc pl-6 mt-1 space-y-1">
                                    {it.sub.map((s, si) => (
                                        <li key={si}>{renderInline(s, `ol${key}-${ii}-${si}`)}</li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    ))}
                </ol>,
            )
            continue
        }

        // Tabla GFM: línea con | seguida de fila separadora ---
        if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
            const header = splitRow(line)
            i += 2
            const rows: string[][] = []
            while (i < lines.length && lines[i].trim().startsWith('|')) {
                rows.push(splitRow(lines[i]))
                i++
            }
            blocks.push(
                <div key={key++} className="overflow-x-auto my-4">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr>
                                {header.map((h, hi) => (
                                    <th key={hi} className="border border-gray-700 px-3 py-2 text-left bg-gray-800">
                                        {renderInline(h, `th${key}-${hi}`)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, ri) => (
                                <tr key={ri}>
                                    {r.map((c, ci) => (
                                        <td key={ci} className="border border-gray-700 px-3 py-2 align-top">
                                            {renderInline(c, `td${key}-${ri}-${ci}`)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>,
            )
            continue
        }

        // Blockquote (puede abarcar varias líneas)
        if (line.startsWith('>')) {
            const quote: string[] = []
            while (i < lines.length && lines[i].startsWith('>')) {
                quote.push(lines[i].replace(/^>\s?/, ''))
                i++
            }
            blocks.push(
                <blockquote key={key++} className="border-l-4 border-amber-500 bg-amber-500/10 pl-4 py-2 my-4 text-amber-200 text-sm">
                    {renderInline(quote.join(' '), `bq${key}`)}
                </blockquote>,
            )
            continue
        }

        // Listas no ordenadas
        if (/^\s*-\s+/.test(line)) {
            const items: string[] = []
            while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*-\s+/, ''))
                i++
            }
            blocks.push(
                <ul key={key++} className="list-disc pl-6 my-3 space-y-1">
                    {items.map((it, ii) => (
                        <li key={ii}>{renderInline(it, `li${key}-${ii}`)}</li>
                    ))}
                </ul>,
            )
            continue
        }

        // Encabezados
        if (line.startsWith('### ')) {
            blocks.push(<h3 key={key++} className="text-lg font-semibold mt-5 mb-2">{renderInline(line.slice(4), `h3${key}`)}</h3>)
            i++; continue
        }
        if (line.startsWith('## ')) {
            blocks.push(<h2 key={key++} className="text-xl font-bold mt-6 mb-2">{renderInline(line.slice(3), `h2${key}`)}</h2>)
            i++; continue
        }
        if (line.startsWith('# ')) {
            blocks.push(<h1 key={key++} className="text-2xl font-bold mt-2 mb-4">{renderInline(line.slice(2), `h1${key}`)}</h1>)
            i++; continue
        }

        // Regla horizontal
        if (/^---+$/.test(line.trim())) {
            blocks.push(<hr key={key++} className="my-5 border-gray-700" />)
            i++; continue
        }

        // Línea en blanco
        if (line.trim() === '') { i++; continue }

        // Párrafo (junta líneas consecutivas)
        const para: string[] = []
        while (i < lines.length && lines[i].trim() !== '' && !/^[#>\-|]/.test(lines[i]) && !/^---+$/.test(lines[i].trim())
            && !lines[i].trim().startsWith('```') && !/^\d+\.\s+/.test(lines[i])) {
            para.push(lines[i])
            i++
        }
        blocks.push(<p key={key++} className="my-3 leading-relaxed text-gray-300">{renderInline(para.join(' '), `p${key}`)}</p>)
    }

    return <div className="text-gray-200">{blocks}</div>
}
